import variables from '../variables/variables'
import { makeAutoObservable } from 'mobx'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import Loader from 'reusable/mobx-helpers/Loader'
import { apiClient, apiClientSilent } from 'api/trpc/trpc-client'
import { signOut, getCurrentUser, fetchAuthSession, fetchUserAttributes, AuthUser, FetchUserAttributesOutput, signInWithRedirect, decodeJWT, JWT, updateUserAttributes, AuthSession } from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import { Hub } from 'aws-amplify/utils';
import { defaultTrialOrgName, notNull } from '@gap/utils/utils'
import RegionRouterStore from 'features/main/RegionRouterStore'
import { UserJobTitleEnum } from '@gap/backend/app-services/user/models/UserJobTitleDto'
import { apiPublicClient } from 'api/trpc-public/trpc-client'
import AuthCustomTokenStore from './AuthCustomTokenStore'
import { Sentry } from 'sentry/sentry'
import { messageInfo } from 'reusable/mui/modals/message-info'
import { snackbarError, snackbarInfo } from 'reusable/mui/snackbar/snackbar'
import { ulid } from 'ulid'
import { SupportedOauthProvider } from '@gap/utils/config/supported-oauth-providers'
import { oauthPaths } from '@gap/utils/auth/oauth-paths'
import { TokenValidationResult } from '../app-services/token/TokenService'
import { CacheHandler } from 'reusable/utils/cache'
import { UserTrialSetupStepEnum } from '@gap/backend/app-services/user/models/UserTrialStepDto'
import userCache from 'cache/userCache'
import { clearAllCachedIDBEntities } from 'cache/idb/IDBEntityCache'
import { UserDto } from '../app-services/user/models/UserDto'
import { LogoutReason } from './logout-reason'

// almostThere counts as finished for users who were sitting on that step when it was removed.
const finishedTrialSteps: string[] = [UserTrialSetupStepEnum.completed, UserTrialSetupStepEnum.almostThere]

export type AuthFields = {
   email: string
   firstName: string
   lastName: string
   password: string
   passwordConfirm: string
   oldPassword: string
   newPassword: string
   newPasswordConfirm: string
   phoneNumber: string
   confirmationCode: string
   mfaCode: string
   mfaSetUpCode: string
   mfaIssuer: string
   mfaChallengeAnswer: string
   mfaAddress: string
}

export type AuthNavigateTo = 'sign in' | 'sign up'

type AuthStatus =
   | 'sign in'
   | 'verify with mfa code'
   | 'mfa setup required'
   | 'new password required'
   | 'sign up'
   | 'confirm sign up'
   | 'forgot password'
   | 'confirm password reset'
   | 'change email'
   | 'confirm email change'
   | 'validating token'
   | 'select org'
   | undefined

type AuthenticationStatus = 'authenticated' | 'not authenticated'

export default class AuthStore {
   sp
   isAmplifyConfigured: boolean = false
   status: AuthStatus = 'sign in'
   authenticationStatus: AuthenticationStatus = 'not authenticated'
   loader = new Loader(false)
   mfaLoader = new Loader(false)
   initialised = false
   logoutReason: LogoutReason | undefined = undefined
   displayPasswordResetSuccess = false
   displayEmailAddressUpdated = false
   isGuest = false
   email: string | undefined = undefined
   // This is used to redirect the user to the trial setup flow when trying to "auto sign in"
   // after refreshing the page before verification
   pendingTrialRedirect = false
   afterSucessFullVerification: (() => void) | undefined
   userInfo?: UserDto.info

   fields = {
      email: '',
      password: '',
      passwordConfirm: '',
      rememberDevice: false
   }

   private userSession?: AuthSession
   private _user?: AuthUser
   private _userAttributes?: FetchUserAttributesOutput

   constructor(sp: AuthStoreParams) {
      this.sp = sp
      this.configureAuthHubListener()
      makeAutoObservable(this)
         ; (window as any).setVerifyState = () => {
            this.status = 'verify with mfa code'
         }
   }

   get user() {
      return this._user
   }

   get userAttributes() {
      return this._userAttributes
   }

   get isAuthenticated() {
      return this.authenticationStatus === 'authenticated'
   }

   get accessToken() {
      return this.userSession?.tokens?.accessToken || localStorage.getItem(variables.gapAccessTokenLocalStorageKey)
   }

   get userIdTokenPayload() {
      return this.userSession?.tokens?.idToken?.payload
   }

   get isXeroUser() {
      return this.userIdTokenPayload
         ? !!((this.userIdTokenPayload.identities as Array<{ providerName?: string }>)?.find(({ providerName }) => providerName === 'Xero'))
         : false;
   }

   /**
    * Returns the user pool client id based on whether the user is a Guest or not.
    * A Guest uses the backendUserPoolClientId and a non-Guest uses the userPoolClientId.
    * These are separated to allow for different refresh token intervals. The backendUserPoolClientId is used by
    * the backend, while the userPoolClientId is used by the frontend (amplify)
    */
   get userPoolClientId() {
      return this.isGuest ? variables.backendUserPoolClientId : variables.appUserPoolClientId
   }

   get cognitoAttributes() {
      return {
         trialStep: this.userAttributes?.['custom:trialStep'],
         trialOrgId: this.userAttributes?.['custom:trialOrgId'],
         userId: this.userAttributes?.['custom:userId'],
         email: this.userAttributes?.['email'],
         jobTitle: this.userAttributes?.['custom:jobTitle'],
         firstName: `${this.isXeroUser ? this.userIdTokenPayload?.given_name ?? '' : this.userAttributes?.given_name ?? ''}`,
         lastName: `${this.isXeroUser ? this.userIdTokenPayload?.family_name ?? '' : this.userAttributes?.family_name ?? ''}`
      }
   }

   get hasFinishedTrialSetup() {
      const trialStep = this.cognitoAttributes?.trialStep
      return !!trialStep && finishedTrialSteps.includes(trialStep)
   }

   get shouldBeInTrialSetupFlow() {
      const trialStep = this.cognitoAttributes?.trialStep
      return this.isAuthenticated && ((!!trialStep && !this.hasFinishedTrialSetup) || this.pendingTrialRedirect)
   }

   set user(user) {
      this._user = user
   }

   signInWithXero = async (params?: Omit<CustomOauthPayload, 'provider'>) => {
      const provider = 'Xero' satisfies SupportedOauthProvider
      try {
         await signInWithRedirect({
            provider: { custom: 'Xero' },
            ...(params ? {
               customState: this.encodeCustomOauthState({ provider, ...params }),
            } : {}),
         })
      } catch (error: unknown) {
         const isError = error instanceof Error
         if (!isError) {
            snackbarError(`${error}`)
            return
         }
         snackbarError(`Error signing in. ${error}`)
      }
   }

   encodeCustomOauthState = (payload: CustomOauthPayload) => btoa(JSON.stringify({ payload, hash: ulid() }))
   decodeCustomOauthState = (input: string): CustomOauthPayload | null => {
      try {
         return JSON.parse(atob(input)).payload
      } catch {
         return null
      }
   }

   checkSessionIsValid = () => {
      try {
         return this.userSession?.tokens
      } catch {
         return false
      }
   }

   configureAmplify = () => {
      // Disable oauth if current route is the callback for the XPM auth flow
      const { pathWithoutRegion } = this.sp.regionRouterStore
      const enableOauth = !pathWithoutRegion.startsWith('/xero-callback')
      const redirectSignIn = (oauthPaths).includes(pathWithoutRegion)
         ? [variables.regionBaseUrl, pathWithoutRegion].join('')
         : variables.regionBaseUrl

      const userPoolId = variables.userPoolId
      const identityPoolId = variables.identityPoolId
      const userPoolClientId = variables.appUserPoolClientId

      Amplify.configure({
         Auth: {
            Cognito: {
               userAttributes: {
                  email: { required: true }
               },
               userPoolId,
               identityPoolId,
               userPoolClientId,
               signUpVerificationMethod: 'code',
               ...(enableOauth ? {
                  loginWith: {
                     oauth: {
                        domain: variables.userPoolDomain,
                        scopes: variables.cognitoOauthScopes,
                        redirectSignIn: [redirectSignIn],
                        redirectSignOut: [variables.regionBaseUrl],
                        responseType: 'code',
                     },
                  }
               } : {})
            },
         },
         API: {
            REST:
            {
               [variables.amplifyApiName]: {
                  region: import.meta.env.VITE_REGION,
                  endpoint: import.meta.env.VITE_API_URL,
               }
            },

         },
      })
      this.isAmplifyConfigured = true
   }

   configureAuthHubListener = () => {
      Hub.listen('auth', ({ payload }) => {
         switch (payload.event) {
            case 'signedIn':
               this.fields.email = ''
               this.fields.password = ''
               this.fields.passwordConfirm = ''
               this.sp.authCustomTokenStore.removeTokens()
               this.fetchAuthSession()
               break
            case 'signedOut':
               this.authenticationStatus = 'not authenticated'
               CacheHandler.clearAll()
               clearAllCachedIDBEntities()
               if (this.status !== 'validating token' && !window.location.pathname.includes('share')) {
                  this.status = 'sign in'
                  this.sp.regionRouterStore.navigate('/')
               }
               break
            case 'tokenRefresh':
               break
            case 'tokenRefresh_failure':
               this.authenticationStatus = 'not authenticated'
               this.status = 'sign in'
               CacheHandler.clearAll()
               this.sp.regionRouterStore.navigate('/')
               this.logoutReason = 'sessionExpired'
               break
            case 'customOAuthState': {
               const decodedData = this.decodeCustomOauthState(payload.data)
               if (decodedData?.provider === 'Xero' && decodedData.referrer === 'marketplace') {
                  this.sp.regionRouterStore.navigate('/signup/xero')
               }
               if (decodedData?.returnPath) {
                  // returnPath should include the region prefix, so we use vanilla navigate()
                  this.sp.regionRouterStore.sp.navigate(decodedData?.returnPath)
               }
               break
            }
            case 'signInWithRedirect':
               break
            case 'signInWithRedirect_failure':
               this.authenticationStatus = 'not authenticated'
               this.status = 'sign in'
               CacheHandler.clearAll()
               this.sp.regionRouterStore.navigate('/')
               break
         }
      })
   }

   signOut = async () => {
      await signOut()
   }

   initialise = async () => {
      await this.fetchAuthSession()
      this.initialised = true
   }

   setAccessTokenExpiryInSentry = (jwt: JWT | string) => {
      try {
         const { payload } = typeof jwt === 'string' ? decodeJWT(jwt) : jwt
         if (payload.exp) {
            const sessionExpires = new Date(payload.exp * 1000).toISOString()
            Sentry.setUserProperties({ sessionExpires })
         }
      } catch { }
   }

   fetchAuthSession = async () => {
      this.userSession = await fetchAuthSession()
      if (this.userSession.tokens) {
         this.setAccessTokenExpiryInSentry(this.userSession.tokens.accessToken)
         let user: AuthUser | undefined

         try {
            user = await getCurrentUser()
         } catch (error: any) {
            if (error && error.name && error.name === 'UserNotFoundException' && variables.stage.startsWith('dev')) {
               // User not found, probably what happend is a full wipe & seed. Clear cache and redirect to login page
               CacheHandler.clearAll()
               await messageInfo('Dev got wiped & seeded', 'New day new me')
               window.location.href = variables.regionBaseUrl
            }
            throw error
         }

         this._user = user
         await this.fetchUserAttributes()
         Sentry.setUserProperties({
            username: user?.username,
         })
         this.disableAccessWithGapTokens() // user logged in using amplify, so remove gap tokens
         this.authenticationStatus = 'authenticated' // make sure user is authenticated (disableAccessWithGapTokens first sets status to not authenticated)
         this.logoutReason = undefined
         this.displayPasswordResetSuccess = false
         this.displayEmailAddressUpdated = false
      } else {
         await this.verifyExistingGapTokens()
      }
   }

   redirectFromInvalidInvitation = (options?: { postJoinUrl?: string }) => {
      if (options?.postJoinUrl) {
         this.sp.regionRouterStore.navigate(options.postJoinUrl)
      } else {
         this.status = 'sign in'
         this.sp.regionRouterStore.navigate('/')
      }

      snackbarInfo('Please log in to your account')
   }

   fetchUserAttributes = async () => {
      this._userAttributes = await fetchUserAttributes()
   }

   checkInviteIsPendingAcceptance = async ({ email }: { email?: string }) => {
      if (!email) {
         return false
      }
      return apiPublicClient.user.inviteIsPendingAcceptance.query({ email })
   }

   navigateToSignIn = () => {
      this.status = 'sign in'
      this.sp.regionRouterStore.navigate(`/`)
   }

   navigateToSignInAfterSuccessfulPasswordReset = () => {
      this.displayPasswordResetSuccess = true
      this.navigateToSignIn()
   }

   navigateToForgotPassword = () => {
      this.sp.regionRouterStore.navigate(`/forgot-password`)
   }

   updateUserFromAmplify = async () => {
      await this.fetchUserAttributes()
      const cognitoUser = await getCurrentUser()

      // The cognitoFirstNameAttribute getter returns an empty string if not available
      const firstName = !!this.cognitoAttributes.firstName ? { firstName: this.cognitoAttributes.firstName } : {}
      const lastName = !!this.cognitoAttributes.lastName ? { lastName: this.cognitoAttributes.lastName } : {}
      const jobTitle = !!this.cognitoAttributes.jobTitle ? { jobTitle: this.cognitoAttributes.jobTitle as UserJobTitleEnum } : {}

      try {
         const isServiceUser = userCache.serviceUser.get()
         if (!isServiceUser) {
            await apiClientSilent.user.updateFromAmplify.mutate({
               ...firstName,
               ...lastName,
               ...jobTitle,
               cognitoUsername: cognitoUser.username,
               email: notNull(this._userAttributes?.email)
            })
         }
      } catch (error: any) {
         if (error !== 'No current user') {
            console.error(error)
         }
      }
   }

   /**
    * Validates the gap generated token for a Guest. If valid, then signs the user in,
    * returns the jwt tokens for storing in local storage and authorises the user to access "Guest" features.
    */
   validateTokenAndAttemptSignIn = async (token: string, userId: string): Promise<TokenValidationResult> => {
      this.status = 'validating token'
      await signOut()

      const result = await apiPublicClient.user.validateCustomTokenAndReturnCognitoTokens.mutate({ token, userId })
      if (!result) {
         return { status: 'invalid' }
      }

      if (result.status === 'valid' && result.idToken && result.accessToken && result.refreshToken) {
         this.sp.authCustomTokenStore.saveTokenSet({ idToken: result.idToken, accessToken: result.accessToken, refreshToken: result.refreshToken })
         this.authoriseGuest(result.refreshToken, userId)
      }

      return { status: result.status }
   }

   setTokens = (tokenSet: { idToken: string, accessToken: string, refreshToken: string }, userId: string) => {
      this.sp.authCustomTokenStore.saveTokenSet({ idToken: tokenSet.idToken, accessToken: tokenSet.accessToken, refreshToken: tokenSet.refreshToken })
      this.authoriseGuest(tokenSet.refreshToken, userId)
   }

   /**
    * Takes the token and user id and validates the token against generated tokens in the db.
    * This does not attempt to sign the user in.
    */
   validateCustomTokenForUser = async (token: string, userId: string): Promise<TokenValidationResult> => {
      this.status = 'validating token'
      await signOut()

      return await apiPublicClient.user.validateTokenForUser.mutate({ token, userId })
   }

   /**
    * Takes the userId in the email link i.e. /auth/validate/:userId/* and validates
    * that this id belongs to the signed in user. Only called for authenticated users.
    */
   validateUserFromEmailLink = async (userId: string) => {
      // Based on current logged in user, check against the userId in the email link
      try {
         const user = await apiClient.user.getMyUser.query()
         return user.id === userId
      } catch {
         return false
      }
   }

   requestNewEmailLink = async (userId: string, token: string, emailId: string): Promise<boolean> => {
      return await apiPublicClient.user.requestNewEmailLink.mutate({ userId, token, emailId })
   }

   updateUserAttributes = async (attributes: Record<string, string>) => {
      await updateUserAttributes({
         userAttributes: attributes
      })
      await this.fetchUserAttributes()
   }

   /**
    * Ensures the user is on the correct trial step.
    * This is called when the user is signed in and the user is in the trial flow.
    */
   ensureUserIsOnCorrectTrialStep = async () => {
      const trialStepFromCognito = this.cognitoAttributes?.trialStep
      const currentPath = this.sp.regionRouterStore.pathWithoutRegion

      // User should be on the trial setup flow, but are not on the trial route
      if (!currentPath.startsWith('/trial')) {
         // Redirect to the trial step we have in cognito
         if (trialStepFromCognito && !finishedTrialSteps.includes(trialStepFromCognito)) {
            this.sp.regionRouterStore.navigate(`/trial/${trialStepFromCognito}`)
            return
         }
      } else {
         // User is on the trial route, but let's make sure they are on the correct step
         const trialStepFromUrl = currentPath === '/trial' ? '' : currentPath.split('/trial/')[1]

         if (trialStepFromUrl === trialStepFromCognito) {
            return
         }

         this.userInfo = await apiClient.user.getMyUserInfo.query()

         if (this.userInfo.orgs?.length === 1 && this.userInfo.orgs[0].name === defaultTrialOrgName) {
            // User has only 1 org and it's the new trial org. Directly continue to setup org page
            return this.sp.regionRouterStore.navigate(`/trial/${UserTrialSetupStepEnum.orgSetup}`)
         }

         this.status = 'select org'
      }
   }

   /**
    * Authorises a Guest to access "Guest" features and starts the token refresh interval
    */
   private authoriseGuest = (refreshToken: string, userId: string) => {
      this.isGuest = true
      this.authenticationStatus = 'authenticated'
      this.sp.authCustomTokenStore.startTokenRefresh(refreshToken, userId)
   }

   /**
    * Attempts to get a user. If successful, the user is authorised to access "Guest" features.
    * If unsuccessful, the user is unauthentiated and the gap tokens are removed.
    */
   private verifyExistingGapTokens = async () => {
      try {
         const gapAccessToken = localStorage.getItem(variables.gapAccessTokenLocalStorageKey)
         if (gapAccessToken) {
            this.setAccessTokenExpiryInSentry(gapAccessToken)
            const user = await apiClientSilent.user.getMyUser.query()

            const gapRefreshToken = localStorage.getItem(variables.gapRefreshTokenLocalStorageKey)
            if (gapRefreshToken) {
               this.authoriseGuest(gapRefreshToken, user.id)
            }
         }
      } catch {
         this.disableAccessWithGapTokens()
      }
   }

   private disableAccessWithGapTokens = () => {
      this.isGuest = false
      this.authenticationStatus = 'not authenticated'
      this.status = 'sign in'
   }
}

interface AuthStoreParams {
   regionRouterStore: RegionRouterStore,
   authCustomTokenStore: AuthCustomTokenStore,
   previousPath: string | undefined
}

type CustomOauthPayload = {
   provider: SupportedOauthProvider
   referrer?: 'marketplace'
   returnPath?: string
}

export const { Provider: AuthStoreProvider, useProvidedStore: useAuthStore } = createStoreContext(AuthStore)
